import * as React from 'react'
import { AppState } from '../../../store'
import { connect } from 'react-redux'
import { ActiveStorageController, UserController } from '../../../controllers'
import Notification from '../../../utilities/Notification'
import { setCurrentUser } from '../../../store/authentication/actions'
import { AnyAction, Dispatch } from 'redux';
import ScrollToTopOnMount from '../../../components/Effects/ScrollToTopOnMount';
import { WithTranslation, withTranslation } from 'react-i18next';
import { setLocale } from '../../../translations';
import LocaleHelper from '../../../helpers/LocaleHelper';
import { CurrentUser, DisplayableError, Me } from '../../../types';
import AvatarEditor from '../../../components/Avatar/AvatarEditor';
import PowerSelect from '../../../components/Form/PowerSelect';
import ReactSelectTheme from '../../../components/Form/ReactSelectTheme';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  setCurrentUser: typeof setCurrentUser
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation

interface IState {
  me: Me
  errors: DisplayableError[]
}

class ProfileDetails extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      me: {
        ...props.currentUser,
      },
      errors: []
    }

    this.onAvatarChange = this.onAvatarChange.bind(this);
    this.onAvatarDeleteClick = this.onAvatarDeleteClick.bind(this);
    this.onFullNameChange = this.onFullNameChange.bind(this);
    this.onLocaleChange = this.onLocaleChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this)
  }

  onFullNameChange(e) {
    const { me } = this.state;
    const name = e.currentTarget.value;

    this.setState({
      me: {
        ...me,
        name: name
      }
    });
  }

  onLocaleChange(option) {
    const { me } = this.state;

    this.setState({
      me: {
        ...me,
        locale: option.value,
      }
    });
  }

  onFormSubmit(e) {
    e.preventDefault();

    const { setCurrentUser, t } = this.props
    const { me } = this.state;

    UserController
      .updateUser(me)
      .then(response => {
        const { errors } = response;

        if (errors && errors.length > 0) {
          this.setState({
            errors: errors
          });

          Notification.notifyError(t('ProfileDetails::Oops something went wrong'))
        }
        else {
          Notification.notifySuccess(t('ProfileDetails::Updated successfully!'));

          setLocale(me.locale)
          setCurrentUser(response)
        }

      })
      .catch(console.error)
  }

  onAvatarChange(acceptedFiles) {
    const avatar = acceptedFiles[0]
    const { setCurrentUser } = this.props

    try {
      ActiveStorageController.uploadPublic(avatar, async (error, blob) => {
        if (error) { console.error(error) }
        if (blob) {
          const updatedMe = await UserController.updateUser({
            ...this.state.me,
            avatar: blob.signed_id
          })

          this.setState({ me: updatedMe })
          setCurrentUser(updatedMe)
        }
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  async onAvatarDeleteClick() {
    const { me } = this.state
    const { setCurrentUser } = this.props

    try {
      const updatedMe = await UserController.updateUser({
        ...me,
        avatar: null
      })

      this.setState({ me: updatedMe })
      setCurrentUser(updatedMe)
    } catch (ex) {
      console.error(ex)
    }
  }

  render() {
    const { t } = this.props
    const { me, errors } = this.state

    const localeOptions = LocaleHelper.getLocaleOptions().filter(option => option.value !== 'de')
    const selectedLocaleOption = localeOptions.find(option => option.value === me.locale)

    return (
      <>
        <ScrollToTopOnMount />
        <form onSubmit={this.onFormSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>{t('ProfileDetails::Profile')}</CardTitle>
              <CardDescription>{t('ProfileDetails::Update your photo and personal details here.')}</CardDescription>
            </CardHeader>
            <CardContent className='flex flex-col gap-6'>
              <Field>
                <FieldLabel>{t('ProfileDetails::Photo')}</FieldLabel>
                <AvatarEditor
                  uri={me.avatar_url}
                  alignLeftOnDesktop
                  onChange={this.onAvatarChange}
                  onDelete={this.onAvatarDeleteClick}
                />
              </Field>

              <div className='grid! grid-cols-1! gap-4 sm:grid-cols-2!'>
                <Field>
                  <FieldLabel htmlFor='profile-details-name'>{t('ProfileDetails::Name')}</FieldLabel>
                  <input
                    id='profile-details-name'
                    type='text'
                    name='name'
                    placeholder='John'
                    value={me.name ? me.name : ''}
                    onChange={this.onFullNameChange}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor='profile-details-locale'>{t('ProfileDetails::Language')}</FieldLabel>
                  <PowerSelect
                    inputId='profile-details-locale'
                    options={localeOptions}
                    value={selectedLocaleOption}
                    onChange={this.onLocaleChange}
                    noOptionsMessage={() => t('ProfileDetails::No options found')}
                    isClearable={false}
                    theme={ReactSelectTheme}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </Field>
              </div>

              {errors.length > 0 && (
                <Alert variant='destructive'>
                  <AlertDescription>
                    {errors.map((error, index) => (
                      <div key={index}>{error.message}</div>
                    ))}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className='justify-end bg-white'>
              <Button type='submit'>{t('ProfileDetails::Save changes')}</Button>
            </CardFooter>
          </Card>
        </form>
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    setCurrentUser: (user: CurrentUser) => dispatch(setCurrentUser(user)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProfileDetails))
